/**
 * Task #220 — "Needs values" badge helper.
 *
 * Shared between ProjectConfigModal (Full Project) and
 * MiniProjectSettingsModal (Mini-Project) so the badge semantics stay
 * identical across the two surfaces.
 *
 * Per Skip's locked decision (interpretation ii): the badge counts
 * **hierarchy-participating fields with no value**. Fields outside any
 * declared hierarchy are not counted, even when empty, because the
 * portfolio governance concern that drove this badge is "this project
 * won't group correctly under the current schema until these are set."
 *
 * The schema model has no `required` boolean today (the DEV-920 validator
 * explicitly allows null leaves). If that changes later, extend this
 * helper to consider `required` as well; the call sites won't need to
 * change.
 */

import { normalizeGuid } from "../../utils/guid";

const safeGuid = (v: any): string => normalizeGuid(v);

/**
 * Compute how many hierarchy-participating fields on the given schema
 * have no value for the named owner. Zero means "no badge."
 *
 * @param ownerGuid     project_guid (FP) or mini_project_guid (MP)
 * @param contentType   "project" | "mini_project" — the metadata-values
 *                      discriminator
 * @param schemaGuid    the schema currently selected on the owner; empty
 *                      string is treated as "no schema" and returns 0
 * @param schemaFields  apiCommunityData.schemaFields (all fields, every schema)
 * @param hierarchies   apiCommunityData.hierarchies (metadata_value variant
 *                      only is examined; entity_walk hierarchies don't read
 *                      project metadata values so a missing value can't
 *                      mis-group those rows)
 * @param metadataValues apiCommunityData.metadataValues (all rows; this
 *                      function filters to the owner + schema)
 */
export function computeMetadataNeedsValuesCount(
  ownerGuid: string | null | undefined,
  contentType: "project" | "mini_project",
  schemaGuid: string | null | undefined,
  schemaFields: Array<Record<string, any>> | null | undefined,
  hierarchies: Array<Record<string, any>> | null | undefined,
  metadataValues: Array<Record<string, any>> | null | undefined,
): number {
  const sg = safeGuid(schemaGuid ?? "");
  const og = safeGuid(ownerGuid ?? "");
  if (!sg || !og) return 0;

  // Collect every field_guid that participates in a metadata-value
  // hierarchy on this schema. entity_walk hierarchies are excluded —
  // their tier sources are entity_type strings, not field_guids on the
  // project's metadata values, so a missing value can't mis-group them.
  const hierarchyFieldGuids = new Set<string>();
  for (const h of hierarchies || []) {
    if (String((h as any)?.valueSource ?? "metadata_value") !== "metadata_value") continue;
    if (safeGuid((h as any)?.schemaGuid) !== sg) continue;
    try {
      const parsed = JSON.parse(String((h as any)?.levelOrder ?? "[]"));
      if (!Array.isArray(parsed)) continue;
      for (const g of parsed) {
        const fg = safeGuid(String(g));
        if (fg) hierarchyFieldGuids.add(fg);
      }
    } catch {
      // malformed level_order on the server — skip rather than crash;
      // a server-side fix is the right path, not a client wrap-around
    }
  }
  if (hierarchyFieldGuids.size === 0) return 0;

  // Sanity-check: every hierarchy-listed field GUID should also exist in
  // the schema's field list. Drop any orphan (deleted field) — it can't
  // be filled in from the UI, so badging on it would be a dead end.
  const schemaFieldGuids = new Set<string>();
  for (const f of schemaFields || []) {
    if (safeGuid((f as any)?.schema_guid) !== sg) continue;
    const fg = safeGuid((f as any)?.field_guid);
    if (fg) schemaFieldGuids.add(fg);
  }
  const alive = new Set<string>();
  for (const fg of hierarchyFieldGuids) {
    if (schemaFieldGuids.has(fg)) alive.add(fg);
  }
  if (alive.size === 0) return 0;

  // Pull the owner's persisted values for this schema. A row is
  // considered "populated" when ANY typed column is non-null/empty.
  // Cleared rows (all columns null) count as missing — they exist in
  // the DB but communicate "no value set" to the user.
  const populated = new Set<string>();
  for (const v of metadataValues || []) {
    const ct = String((v as any)?.content_type ?? "project");
    if (ct !== contentType) continue;
    if (safeGuid((v as any)?.schema_guid) !== sg) continue;
    const owner =
      contentType === "mini_project"
        ? safeGuid((v as any)?.mini_project_guid ?? "")
        : safeGuid((v as any)?.project_guid ?? "");
    if (owner !== og) continue;
    const fg = safeGuid((v as any)?.field_guid);
    if (!fg || !alive.has(fg)) continue;
    const anySet =
      (v as any)?.value_string != null && (v as any)?.value_string !== "" ||
      typeof (v as any)?.value_number === "number" ||
      typeof (v as any)?.value_boolean === "boolean" ||
      ((v as any)?.value_date != null && (v as any)?.value_date !== "") ||
      ((v as any)?.value_enum != null && (v as any)?.value_enum !== "") ||
      ((v as any)?.value_guid != null && (v as any)?.value_guid !== "");
    if (anySet) populated.add(fg);
  }

  return alive.size - populated.size;
}
